import React, { useState } from 'react';
import { Table, TableColumn } from 'ds/components/table';
import { useLocale } from 'locale';
import { WithContentSlotSidePaddingContainer } from 'ds/components/Layout';
import { InsightType } from 'utils/entities';
import { ShowAt } from 'consts/breakpoints';
import { ViolationsSummaryWrapper } from './styled';
import { getDateColumnConfig } from './columns/DateColumn';
import { getSeverityColumnConfig } from './columns/SeverityColumn';
import { getWhereColumnConfig } from './columns/WhereColumn';
import { getTypeColumnConfig } from './columns/ViolationTypeColumn';
import { Tabs } from 'ds/components/tabs';
import { AccordionTabsHeader } from 'components/widget/transportation/styled';
import { SectionType } from 'components/widget/common/styled';
import { SmallText, Text } from 'ds/components/typography';
import { PricesTabsBodyWrapper } from 'components/widget/prices/styled';
import { HpdViolation } from 'store/state/selectors/insights/summaryTypes';

interface ViolationsSummaryProps {
  data: HpdViolation[];
}

enum Tab {
  Open = 'open',
  Closed = 'closed',
}

const TABS: Tab[] = [ Tab.Open, Tab.Closed ];

export const ViolationsSummary: React.FunctionComponent<ViolationsSummaryProps> = ({ data }) => {
  const { t } = useLocale();
  const [ activeTab, setActiveTab ] = useState<Tab>(Tab.Open);

  const renderTable = (isMobile: boolean) => {
    const tabData = data.filter(i => activeTab === Tab.Open ? i.open : !i.open);
    if (!tabData.length) return null;

    let columns = [];

    const dateCol = {
      label: t('violationsInsightSummary.when'),
      ...getDateColumnConfig(isMobile),
    };

    const whereCol = {
      label: t('violationsInsightSummary.where'),
      ...getWhereColumnConfig(),
    };

    const typeCol = {
      label: t('violationsInsightSummary.violationType'),
      ...getTypeColumnConfig(isMobile),
    };

    const severityCol = {
      label: t('violationsInsightSummary.severity'),
      ...getSeverityColumnConfig(),
    };

    if (isMobile) columns = [ dateCol, typeCol ];
    else columns = [ dateCol, whereCol, typeCol, severityCol ];

    return (
      <Table<HpdViolation>
        paginationType="infinite"
        data={tabData}
        visibleRows={isMobile ? 8 : 6}
        insightType={InsightType.Nuisances}
        columns={columns as Array<TableColumn<HpdViolation>>}
      />
    );
  };

  return (
    <WithContentSlotSidePaddingContainer>
      <ViolationsSummaryWrapper>
        <Tabs
          activeTab={TABS.indexOf(activeTab)}
          setActiveTab={(id) => setActiveTab(TABS[id])}
          headerWrapper={AccordionTabsHeader}
          headers={TABS.map((tab, id) => (
            <SectionType
              style={{ minWidth: 'auto' }}
              key={tab}
              isActive={tab === activeTab}
              type={InsightType.Nuisances}
            >
              <ShowAt from={2}>
                <Text>
                  {t('violationsInsightSummary.tabLabel', { tab, amount: data.filter(i => tab === Tab.Open ? i.open : !i.open).length })}
                </Text>
              </ShowAt>
              <ShowAt at={1}>
                <SmallText>
                  {t('violationsInsightSummary.tabLabel', { tab, amount: data.filter(i => tab === Tab.Open ? i.open : !i.open).length })}
                </SmallText>
              </ShowAt>
            </SectionType>
          ))}
          bodyWrapper={PricesTabsBodyWrapper}
          bodies={TABS.map(tab => (
            <ShowAt at={1}>
              {matches => renderTable(matches)}
            </ShowAt>
          ))}
        />
      </ViolationsSummaryWrapper>
    </WithContentSlotSidePaddingContainer>
  );
};
